import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import LocationsFAQ from './components/LocationsFAQ';
import WhyChooseUs from '../home/components/WhyChooseUs';
import Testimonials from '../home/components/Testimonials';
import ServiceArea from '../home/components/ServiceArea';
import CTASection from '../home/components/CTASection';
import Gallery from '../home/components/Gallery';
import ConsultationForm from '../home/components/ConsultationForm';

const majorCities = [
  {
    city: 'Boston & Greater Boston',
    state: 'Massachusetts',
    flag: 'ri-building-2-line',
    description: 'Our flagship Wellesley Hills studio serves the entire Greater Boston area. Clients from Boston, Newton, Brookline, Cambridge, Dedham, Needham, and surrounding towns visit our studio or request a house call at their convenience.',
    services: ['Custom Suits & Shirts', 'Expert Alterations', 'Style Consultation', 'Wedding Formal Wear', 'In-Studio & House Calls'],
    keywords: 'Custom tailor Boston, bespoke suits Boston MA, tailoring Wellesley Hills',
    image: 'https://public.readdy.ai/ai/img_res/e38688944702ffc5c230fea3b34a1e28.jpg',
  },
  {
    city: 'New York City',
    state: 'New York',
    flag: 'ri-building-3-line',
    description: 'We serve Manhattan, Brooklyn, the Upper East Side, Midtown, the Financial District, and across the New York metro area — at your home, apartment, or office. No need to find a tailor in New York when we come to you.',
    services: ['Custom Bespoke Suits', 'Corporate Wardrobe Programs', 'Shirt Tailoring', 'Alterations', 'Office & Apartment Calls'],
    keywords: 'Custom tailor New York, bespoke suit NYC, tailor Manhattan',
    image: 'https://readdy.ai/api/search-image?query=Manhattan%20New%20York%20City%20skyline%20viewed%20from%20across%20the%20East%20River%20during%20golden%20hour%2C%20warm%20amber%20light%20on%20skyscrapers%2C%20elegant%20urban%20professional%20city%20atmosphere%2C%20clean%20dramatic%20sky&width=600&height=400&seq=loc-nyc-02&orientation=landscape',
  },
  {
    city: 'Los Angeles',
    state: 'California',
    flag: 'ri-sun-line',
    description: 'From Beverly Hills and Bel Air to Santa Monica, Westwood, and the Hollywood Hills — our consultants regularly travel throughout Los Angeles and Southern California to serve discerning clients in the entertainment, business, and creative industries.',
    services: ['Custom Suits & Shirts', 'Red Carpet Formal Wear', 'Style Consultation', 'Alterations', 'House & Hotel Calls'],
    keywords: 'Custom tailor Los Angeles, bespoke suits Beverly Hills, tailor LA',
    image: 'https://readdy.ai/api/search-image?query=Los%20Angeles%20California%20skyline%20with%20palm%20trees%20in%20foreground%20at%20sunset%2C%20warm%20golden%20orange%20and%20pink%20sky%2C%20iconic%20upscale%20California%20city%20atmosphere%2C%20premium%20and%20glamorous%20feel&width=600&height=400&seq=loc-la-03&orientation=landscape',
  },
  {
    city: 'Chicago',
    state: 'Illinois',
    flag: 'ri-windy-line',
    description: 'The Windy City has long been a hub for business and style. We serve clients throughout Chicago — from the Gold Coast and Lincoln Park to River North, Oak Park, and the northern suburbs — with full house call tailoring services.',
    services: ['Custom Suits', 'Business Wardrobe', 'Alterations', 'Wedding Formal Wear', 'House & Office Calls'],
    keywords: 'Custom tailor Chicago, bespoke suits Chicago IL, tailoring Gold Coast',
    image: 'https://readdy.ai/api/search-image?query=Chicago%20Illinois%20iconic%20downtown%20skyline%20along%20Lake%20Michigan%20at%20blue%20hour%2C%20dramatic%20architectural%20silhouettes%2C%20deep%20blue%20sky%20reflections%20on%20the%20lake%2C%20sophisticated%20urban%20professional%20atmosphere&width=600&height=400&seq=loc-chi-04&orientation=landscape',
  },
  {
    city: 'Miami & South Florida',
    state: 'Florida',
    flag: 'ri-water-flash-line',
    description: 'We serve clients throughout South Florida — Miami Beach, Coral Gables, Boca Raton, Fort Lauderdale, Palm Beach, and beyond. Our consultants travel to your home, office, or yacht to deliver the full British Custom Tailors experience.',
    services: ['Custom Linen & Tropical Suits', 'Formal Wear', 'Style Consultation', 'Alterations', 'Residence & Yacht Calls'],
    keywords: 'Custom tailor Miami, bespoke suits South Florida, tailor Boca Raton',
    image: 'https://readdy.ai/api/search-image?query=Miami%20Florida%20skyline%20and%20Biscayne%20Bay%20at%20sunset%20with%20warm%20tropical%20golden%20orange%20sky%2C%20luxury%20waterfront%20cityscape%2C%20upscale%20resort%20and%20finance%20district%20atmosphere%2C%20vibrant%20and%20elegant&width=600&height=400&seq=loc-mia-05&orientation=landscape',
  },
  {
    city: 'Washington, D.C.',
    state: 'District of Columbia',
    flag: 'ri-government-line',
    description: 'Our nation\'s capital demands impeccable attire. We serve clients across Washington D.C., Maryland, and Northern Virginia — including government officials, attorneys, lobbyists, and executives who require garments that project confidence and authority.',
    services: ['Executive Custom Suits', 'Formal & Ceremonial Wear', 'Shirt Tailoring', 'Alterations', 'Office & Home Calls'],
    keywords: 'Custom tailor Washington DC, bespoke suits DC, tailor Northern Virginia',
    image: 'https://readdy.ai/api/search-image?query=Washington%20DC%20iconic%20National%20Mall%20with%20Capitol%20building%20in%20background%20during%20golden%20hour%2C%20professional%20and%20distinguished%20government%20city%20atmosphere%2C%20warm%20light%20on%20classic%20architecture%2C%20elegant%20and%20powerful&width=600&height=400&seq=loc-dc-06&orientation=landscape',
  },
];

const regions = [
  { name: 'New England', states: 'MA, CT, RI, NH, VT, ME' },
  { name: 'Mid-Atlantic', states: 'NY, NJ, PA, DE, MD' },
  { name: 'Southeast', states: 'FL, GA, NC, SC, VA, TN' },
  { name: 'Midwest', states: 'IL, OH, MI, IN, WI, MN' },
  { name: 'Southwest', states: 'TX, AZ, NV, NM, CO' },
  { name: 'West Coast', states: 'CA, WA, OR, HI' },
];

const houseCallSteps = [
  {
    step: '01',
    icon: 'ri-phone-line',
    title: 'Call or Book Online',
    desc: 'Contact us by phone at 866-857-8487. We\'ll confirm your location and match you with the nearest available consultant.',
  },
  {
    step: '02',
    icon: 'ri-calendar-check-line',
    title: 'We Schedule Your Visit',
    desc: 'We arrange a date and time that works perfectly for you — evenings and weekends available nationwide.',
  },
  {
    step: '03',
    icon: 'ri-car-line',
    title: 'Your Consultant Arrives',
    desc: 'Our expert arrives at your door with 200+ fabric swatches, style guides, and professional measuring tools.',
  },
  {
    step: '04',
    icon: 'ri-ruler-2-line',
    title: 'Consultation & Measurements',
    desc: 'We take your measurements, explore your preferences, and guide you through every style and fabric option.',
  },
  {
    step: '05',
    icon: 'ri-scissors-2-line',
    title: 'Your Garment Is Crafted',
    desc: 'Our master tailors handcraft your garment using traditional techniques at our studio.',
  },
  {
    step: '06',
    icon: 'ri-gift-2-line',
    title: 'Delivered to Your Door',
    desc: 'Your finished garment is pressed and delivered to you. Adjustments are made until your fit is flawless.',
  },
];

export default function LocationsPage() {
  const scrolled = true;

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} />

      {/* ── 1. HERO ── */}
      <section className="relative min-h-[580px] pt-32 flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=wide%20aerial%20panoramic%20view%20of%20multiple%20American%20city%20skylines%20collage%20at%20golden%20hour%20dusk%2C%20Boston%20New%20York%20Los%20Angeles%20Chicago%20glowing%20urban%20lights%2C%20warm%20amber%20and%20deep%20blue%20sky%20tones%2C%20nationwide%20premium%20service%20concept%2C%20high%20altitude%20dramatic%20cityscape%20overview&width=1440&height=580&seq=locations-hero-fix-2026&orientation=landscape')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-black/50 to-black/65" />
        <div className="relative z-10 text-center w-full px-4 py-16">
          <p className="text-[#C9A961] text-xs font-bold tracking-widest uppercase mb-5">Nationwide Service</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            We Bring the Tailor <br /><span className="text-[#C9A961]">To You — Anywhere</span>
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto mb-10 leading-relaxed">
            Flagship studio in Wellesley Hills, Massachusetts. House call service covering all 50 states. One phone call is all it takes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:866-857-8487"
              className="inline-flex items-center justify-center bg-[#C9A961] text-white px-10 py-4 rounded-full text-base font-semibold hover:bg-[#B89851] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call 866-857-8487
            </a>
            <a
              href="#flagship-studio"
              className="inline-flex items-center justify-center border border-white/40 text-white px-10 py-4 rounded-full text-base font-semibold hover:border-[#C9A961] hover:text-[#C9A961] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Find Your Location
              <i className="ri-arrow-down-line ml-2"></i>
            </a>
          </div>
        </div>
      </section>

      {/* ── 2. TRUST BAR — Service Coverage ── */}
      <section className="py-8 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-6">
            <div className="text-gray-400 text-sm font-medium">Serving clients in:</div>
            {regions.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <span className="w-2 h-2 rounded-full bg-[#C9A961]"></span>
                <span className="text-white font-medium">{r.name}</span>
                <span className="text-gray-500">({r.states})</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. SERVICES — Major Cities We Regularly Visit ── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#C9A961] text-xs font-bold tracking-widest uppercase mb-3">Where We Serve</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Major Cities We <span className="text-[#C9A961]">Regularly Visit</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our consultants travel to these cities and surrounding areas on a regular basis — and can reach virtually any city nationwide
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {majorCities.map((city, i) => (
              <article key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#C9A961]/30 transition-all duration-300 group">
                <div className="h-48 overflow-hidden">
                  <img
                    src={city.image}
                    alt={`Custom tailoring in ${city.city}, ${city.state}`}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-7">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {city.city}
                      </h3>
                      <p className="text-[#C9A961] text-sm font-semibold">{city.state}</p>
                    </div>
                    <div className="w-10 h-10 flex items-center justify-center bg-[#C9A961]/10 rounded-xl flex-shrink-0">
                      <i className={`${city.flag} text-[#C9A961] text-lg`}></i>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-5">{city.description}</p>
                  <div className="space-y-1.5 mb-6">
                    {city.services.map((svc, j) => (
                      <div key={j} className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-check-line text-[#C9A961]"></i>
                        {svc}
                      </div>
                    ))}
                  </div>
                  <a
                    href="tel:866-857-8487"
                    className="inline-flex items-center text-[#C9A961] font-semibold text-sm hover:text-[#B89851] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Book in {city.city.split(' ')[0]}
                    <i className="ri-arrow-right-line ml-1.5"></i>
                  </a>
                </div>
              </article>
            ))}
          </div>
          <div className="text-center mt-12">
            <p className="text-gray-500 mb-4">Don't see your city? We likely serve your area.</p>
            <a
              href="tel:866-857-8487"
              className="inline-flex items-center bg-[#C9A961] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#B89851] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call to Confirm Your Area
            </a>
          </div>
        </div>
      </section>

      {/* ── 4. WHY CHOOSE US ── */}
      <WhyChooseUs />

      {/* ── 5. PROCESS — House Call Experience ── */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#C9A961] text-xs font-bold tracking-widest uppercase mb-3">How It Works</p>
            <h2 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Your House Call <span className="text-[#C9A961]">Experience</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Getting a custom suit made has never been simpler — we handle everything from start to delivery
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {houseCallSteps.map((step) => (
              <div key={step.step} className="bg-white/5 border border-white/10 rounded-2xl p-7 hover:border-[#C9A961]/40 transition-all duration-300">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#C9A961] flex-shrink-0">
                    <i className={`${step.icon} text-xl text-white`}></i>
                  </div>
                  <span className="text-4xl font-bold text-white/10" style={{ fontFamily: 'Playfair Display, serif' }}>{step.step}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {step.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 6. ABOUT — Flagship Studio ── */}
      <section id="flagship-studio" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="inline-flex items-center bg-[#C9A961]/10 text-[#C9A961] px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider mb-6">
                <i className="ri-store-2-line mr-2"></i>
                Our Flagship Studio
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Wellesley Hills, <span className="text-[#C9A961]">Massachusetts</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-5">
                Our home base and flagship studio is located in the heart of <strong className="text-gray-800">Wellesley Hills, MA</strong> — one of New England's most distinguished communities. Here, you will find our complete fabric library, our full team of master tailors, and the premier in-person British Custom Tailors experience.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Visitors to our studio receive a private, unhurried consultation with an expert tailor — surrounded by hundreds of fabric samples and decades of tailoring heritage. We serve clients from Boston, Newton, Brookline, Natick, Needham, Dedham, and across Greater New England.
              </p>
              <div className="space-y-5 mb-8">
                {[
                  { icon: 'ri-map-pin-line', label: 'Address', value: 'Wellesley Hills, MA 02481' },
                  { icon: 'ri-phone-line', label: 'Phone', value: '866-857-8487', href: 'tel:866-857-8487' },
                  { icon: 'ri-mail-line', label: 'Email', value: 'info@britishtailors.com', href: 'mailto:info@britishtailors.com' },
                  { icon: 'ri-time-line', label: 'Hours', value: 'By Appointment — Monday through Saturday' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#C9A961]/10 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-[#C9A961] text-base`}></i>
                    </div>
                    <div>
                      <div className="text-xs text-gray-400 font-semibold uppercase tracking-wider">{item.label}</div>
                      {item.href ? (
                        <a href={item.href} className="text-gray-800 font-semibold hover:text-[#C9A961] transition-colors cursor-pointer">{item.value}</a>
                      ) : (
                        <div className="text-gray-800 font-semibold">{item.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                <a
                  href="tel:866-857-8487"
                  className="inline-flex items-center bg-[#C9A961] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#B89851] transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Book Studio Visit
                </a>
                <a
                  href="https://maps.google.com/?q=Wellesley+Hills+MA+02481"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center border border-gray-300 text-gray-700 px-8 py-4 rounded-full font-semibold hover:border-[#C9A961] hover:text-[#C9A961] transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-map-2-line mr-2"></i>
                  Get Directions
                </a>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden h-[500px] border border-gray-100">
              <iframe
                title="British Custom Tailors - Wellesley Hills, Massachusetts"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d47188.7!2d-71.2898!3d42.3097!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89e38312b2857e0d%3A0x21af6e67c9db8cd3!2sWellesley%20Hills%2C%20MA%2002481!5e0!3m2!1sen!2sus!4v1680000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* ── 7. TESTIMONIALS ── */}
      <Testimonials />

      {/* ── 8. SERVICE AREA ── */}
      <ServiceArea />

      {/* ── 9. CTA SECTION ── */}
      <CTASection />

      {/* ── 10. GALLERY ── */}
      <Gallery />

      {/* ── 11. SPECIAL OFFER / PAGE CTA ── */}
      <section className="py-24 bg-[#C9A961]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Schedule Your Consultation?
          </h2>
          <p className="text-xl text-white/85 mb-10">
            Whether you visit our Wellesley Hills studio or we come to you — a perfect fit is just one call away.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:866-857-8487"
              className="inline-flex items-center justify-center bg-white text-[#C9A961] px-10 py-4 rounded-full text-lg font-bold hover:bg-gray-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call 866-857-8487
            </a>
            <Link
              to="/services"
              className="inline-flex items-center justify-center border-2 border-white text-white px-10 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-[#C9A961] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Explore All Services
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* ── 12. FAQ ── */}
      <LocationsFAQ />

      {/* ── 13. CONSULTATION FORM ── */}
      <ConsultationForm />

      <Footer />
    </div>
  );
}
